import { Fragment } from "react"
import { makeStyles } from "tss-react/mui"
import { Divider, Stack, Typography } from "@mui/material"
import { faCalendar } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { useMoney } from "../../../../../../modules/currency/useMoney"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { OrderSummaryItemBox } from "../../drawer/orderSummary/OrderSummaryItemBox"

import type { TakeawayCartItem, TakeawayProduct } from "../takeawayTypes"
import { calcItemsTotal } from "./takeawayCheckoutLib"
import { TakeawayCartItemRow } from "./TakeawayCartItemRow"

const useStyles = makeStyles()((theme) => ({
    outerCard: {
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".75rem",
        padding: "1.5rem 1rem",
        color: theme.palette.text.primary,
    },
    dateIcon: {
        fontSize: "1.125rem",
        flexShrink: 0,
        color: theme.palette.tailwind.slate[400],
    },
    dateBold: {
        fontWeight: 700,
    },
    totalLabel: {
        fontWeight: 400,
        color: theme.palette.text.secondary,
    },
}))

type TakeawayBasketDateCardProps = Readonly<{
    dateKey: string
    items: TakeawayCartItem[]
    moneyFactory: ReturnType<typeof useMoney>
    onQuantityChange: (product: TakeawayProduct, quantity: number, dateKey?: string) => void
}>

export function TakeawayBasketDateCard({
    dateKey,
    items,
    moneyFactory,
    onQuantityChange,
}: TakeawayBasketDateCardProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const date = dateTimeFactory.fromISO(dateKey)
    const dateTotal = calcItemsTotal(items)

    return (
        <Stack gap="1rem" className={classes.outerCard}>
            <OrderSummaryItemBox
                title={
                    <Stack direction="row" gap=".5rem" alignItems="center">
                        <FontAwesomeIcon icon={faCalendar} className={classes.dateIcon} />
                        <LocalizedStrict id="takeaway-basket-date-card-delivery-date-label">
                            <Typography variant="body1" component="span">
                                Delivery date:
                            </Typography>
                        </LocalizedStrict>
                        <Typography variant="body1" component="span" className={classes.dateBold}>
                            {date.toFormat("d MMMM yyyy")}
                        </Typography>
                    </Stack>
                }
            >
                {items.map((item, index) => (
                    <Fragment key={item.product.id}>
                        {index > 0 && <Divider flexItem />}
                        <TakeawayCartItemRow
                            item={item}
                            moneyFactory={moneyFactory}
                            onQuantityChange={(product, quantity) => onQuantityChange(product, quantity, dateKey)}
                        />
                    </Fragment>
                ))}
            </OrderSummaryItemBox>

            {dateTotal && (
                <Stack direction="row" gap="1rem" justifyContent="space-between" alignItems="flex-end">
                    <LocalizedStrict id="takeaway-checkout-total-label">
                        <Typography variant="h6" className={classes.totalLabel}>
                            Total
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="h4">{moneyFactory.format(dateTotal)}</Typography>
                </Stack>
            )}
        </Stack>
    )
}
